import { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Chip, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Select, MenuItem, FormControl, InputLabel, Typography, Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import API from "../../../../api";
import { colors } from "../../../../theme";
import ClassificationTreePicker from "./ClassificationTreePicker";
import { humanizeTaxonomy } from "./assetClassification";

// ── Fields written straight to the document (dashboard-managed corrections) ──
// vendor/vlan/zone.name/zone.purdue_level used to be editable here too, but
// they're all probe-observed values that the next scan resyncs from live
// network data — a manual edit looked like it saved (the request succeeds)
// and then got silently overwritten on the next scan, which is not what
// "edit" should mean. Zone is still editable, just through its own dedicated
// flow (ZoneManagerDialog), not here. Notes moved to inline editing on the
// Identity tab's own Notes card (NotesCard in AssetDetailContent.jsx) instead
// of this dialog, closer to the text it edits.
const DB_FIELD_DEFS = [
  { key: "machine_name",      label: "Custom Name",  getValue: (r) => r.machine_name, clearable: true },
  { key: "device.firmware",   label: "Firmware",     getValue: (r) => r.device?.firmware },
];
const emptyDbForm = () => Object.fromEntries(DB_FIELD_DEFS.map((f) => [f.key, ""]));

// ── Override groups ──────────────────────────────────────────────────────────
// One Select per group. The Select value is either a preset the user picked, the
// sentinels KEEP / CLEAR / CUSTOM, or the empty string (no selection). Only the
// non-KEEP groups make it into the request; CUSTOM reveals a text input for a
// free-text value.
const KEEP   = "__keep__";
const CLEAR  = "__clear__";
const CUSTOM = "__custom__";

const OS_FAMILY_PRESETS = ["Windows", "Linux", "macOS", "FreeBSD", "Android", "iOS", "Unix", "VMware ESXi"];
const DEPLOYMENT_PRESETS = ["physical", "virtual_machine", "container", "hypervisor_host"];

// Classification picking now goes straight through ClassificationTreePicker
// (searchable, grouped by level, sourced from assetClassification.js's own
// TAXONOMY_PATHS) instead of a hand-picked flat preset list — every
// combination the taxonomy defines is reachable, not just ~50 hand-copied
// entries that could drift from it.

// A minimal, English-only pin group. `value` is the Select choice; `custom` /
// `customCls` hold the free-text state that appears only when the user picks
// "Custom…" — this is what the request payload reads from.
const emptyPin = () => ({
  os_family:      { value: KEEP, custom: "" },
  os_version:     { value: KEEP, custom: "" },
  os_build:       { value: KEEP, custom: "" },
  classification: { value: KEEP, customCls: { asset_type: "", subcategory: "", type: "" } },
  deployment:     { value: KEEP },
});

// ── One reusable Select row ──────────────────────────────────────────────────
// Module-scope, not defined inside EditAssetDialog: a component declared
// inside another component's render body is a NEW function identity every
// render, so React treats it as a different component type at that position
// and remounts the whole subtree on every keystroke/click — invisible for a
// plain MUI Select (no internal state to lose) but it silently reset every
// branch's expand/collapse state back to closed in ClassificationTreePicker
// below, which looked like "clicking a tree node collapses the tree".
const OverrideSelect = ({ label, pinKey, presets, placeholder, g, currentValue, onChange }) => (
  <Box>
    <FormControl size="small" fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        value={g.value}
        onChange={(e) => onChange({ value: e.target.value })}
        MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
      >
        <MenuItem value={KEEP}>
          <em>Keep current{currentValue ? ` — ${currentValue}` : ""}</em>
        </MenuItem>
        {presets && presets.map((p) => (
          <MenuItem key={p} value={p}>{p}</MenuItem>
        ))}
        <MenuItem value={CLEAR}>Reset to automatic</MenuItem>
        <MenuItem value={CUSTOM}>Custom value…</MenuItem>
      </Select>
    </FormControl>
    {g.value === CUSTOM && (
      <TextField
        size="small"
        fullWidth
        placeholder={placeholder}
        value={g.custom}
        onChange={(e) => onChange({ custom: e.target.value })}
        sx={{ mt: 1 }}
        autoFocus
      />
    )}
  </Box>
);

const CLASSIFICATION_MODE_OPTIONS = [
  { value: KEEP, label: "Keep current" },
  { value: CLEAR, label: "Reset to automatic" },
  { value: CUSTOM, label: "Custom value…" },
];

const ClassificationSelect = ({ g, row, onChange }) => {
  // `classification.type` (singular) hasn't been the real field for a while —
  // the canonical shape is the additive `classification.types[]` (a device can
  // carry more than one role at once); the most specific single value to show
  // here is the highest-confidence entry in that array, not a field that's
  // never actually populated.
  const bestType = Array.isArray(row?.classification?.types)
    ? row.classification.types.reduce((best, t) => (
        t?.value && (!best || (t.confidence ?? -1) > (best.confidence ?? -1)) ? t : best
      ), null)
    : null;
  const current = bestType?.value
    || row?.classification?.subcategory?.value
    || row?.classification?.asset_type?.value
    || null;

  // A picked taxonomy path (e.g. "ot/industrial_control/plc") lives in the
  // same g.value slot as the KEEP/CLEAR/CUSTOM sentinels — it's "none of the
  // three sentinels" rather than its own separate mode, so the tree stays
  // visible (and the previously-picked node stays highlighted) whenever the
  // user isn't explicitly on Keep/Reset/Custom.
  const isPathSelected = g.value !== KEEP && g.value !== CLEAR && g.value !== CUSTOM;

  return (
    <Box>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: colors.grey[400], mb: 0.5 }}>
        Classification{current ? ` — currently ${humanizeTaxonomy(current)}` : ""}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1 }}>
        {CLASSIFICATION_MODE_OPTIONS.map((opt) => (
          <Box
            key={opt.value}
            onClick={() => onChange({ value: opt.value })}
            sx={{
              px: 1.2, py: 0.5, borderRadius: 999, cursor: "pointer", fontSize: 11.5, fontWeight: 600,
              bgcolor: g.value === opt.value ? "#45909533" : "transparent",
              color: g.value === opt.value ? "#7de8ed" : colors.grey[400],
              border: `1px solid ${g.value === opt.value ? "#459095" : colors.grey[800]}`,
            }}
          >
            {opt.label}
          </Box>
        ))}
      </Box>

      {g.value !== CUSTOM && (
        <ClassificationTreePicker
          value={isPathSelected ? g.value : ""}
          onChange={(path) => onChange({ value: path })}
        />
      )}

      {g.value === CUSTOM && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 1, mt: 1 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={g.customCls.asset_type}
              onChange={(e) => onChange({ customCls: { ...g.customCls, asset_type: e.target.value } })}
            >
              <MenuItem value=""><em>—</em></MenuItem>
              <MenuItem value="it">IT</MenuItem>
              <MenuItem value="ot">OT</MenuItem>
              <MenuItem value="iot">IoT</MenuItem>
              <MenuItem value="iomt">IoMT</MenuItem>
              <MenuItem value="network">Network</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small" label="Subcategory" placeholder="server, control…"
            value={g.customCls.subcategory}
            onChange={(e) => onChange({ customCls: { ...g.customCls, subcategory: e.target.value } })}
          />
          <TextField
            size="small" label="Type" placeholder="domain_controller…"
            value={g.customCls.type}
            onChange={(e) => onChange({ customCls: { ...g.customCls, type: e.target.value } })}
          />
        </Box>
      )}
    </Box>
  );
};

const EditAssetDialog = ({ row, open, onClose, onSave, onOverrideSaved }) => {
  const [dbForm, setDbForm] = useState(emptyDbForm());
  const [pin, setPin] = useState(emptyPin());
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (open && row) {
      setDbForm(Object.fromEntries(DB_FIELD_DEFS.map((f) => [f.key, f.getValue(row) ?? ""])));
      setPin(emptyPin());
      setMessage(null);
    }
  }, [open, row]);

  const setDb = (key) => (e) => setDbForm((f) => ({ ...f, [key]: e.target.value }));
  const setPinGroup = (key, patch) => setPin((p) => ({ ...p, [key]: { ...p[key], ...patch } }));

  const dbChangedCount = DB_FIELD_DEFS.filter(({ key, clearable, getValue }) => {
    const cur = String(dbForm[key] ?? "").trim();
    const og  = String(getValue(row ?? {}) ?? "");
    return clearable ? cur !== og : (cur !== "" && cur !== og);
  }).length;

  // A group counts as active if it isn't on KEEP. CUSTOM without a value is
  // still active but will fail submit validation below.
  const activeGroups = useMemo(() => ["os_family", "os_version", "os_build", "classification", "deployment"]
    .filter((k) => pin[k].value !== KEEP), [pin]);

  // Submit gate: at least one non-KEEP group or at least one DB field changed,
  // AND every CUSTOM group has a value.
  const customIncomplete = ["os_family", "os_version", "os_build"].some(
    (k) => pin[k].value === CUSTOM && !pin[k].custom.trim()
  ) || (pin.classification.value === CUSTOM && !(
    pin.classification.customCls.asset_type.trim() ||
    pin.classification.customCls.subcategory.trim() ||
    pin.classification.customCls.type.trim()
  ));

  const canSubmit =
    !saving && !pending && !customIncomplete &&
    (dbChangedCount > 0 || activeGroups.length > 0);

  const buildOverridePayload = (oid) => {
    const p = { oid };

    for (const k of ["os_family", "os_version", "os_build"]) {
      const g = pin[k];
      if (g.value === CLEAR) p[`clear_${k}`] = true;
      else if (g.value === CUSTOM) {
        if (g.custom.trim()) p[k] = g.custom.trim();
      } else if (g.value !== KEEP && g.value) {
        p[k] = g.value;
      }
    }

    const c = pin.classification;
    if (c.value === CLEAR) p.clear_classification = true;
    else if (c.value === CUSTOM) {
      if (c.customCls.asset_type.trim())  p.class_asset_type  = c.customCls.asset_type.trim();
      if (c.customCls.subcategory.trim()) p.class_subcategory = c.customCls.subcategory.trim();
      if (c.customCls.type.trim())        p.class_type        = c.customCls.type.trim();
    } else if (c.value !== KEEP && c.value) {
      // c.value is a taxonomy path from ClassificationTreePicker, e.g.
      // "ot/industrial_control/plc" — any level can be the final pick (the
      // taxonomy itself treats an absent subcategory/type as "the evidence
      // doesn't reach that far", not incomplete), so only the levels actually
      // present in the path are sent.
      const [assetType, subcategory, type] = c.value.split("/");
      if (assetType)  p.class_asset_type  = assetType;
      if (subcategory) p.class_subcategory = subcategory;
      if (type)        p.class_type        = type;
    }
    const d = pin.deployment;
    if (d.value === CLEAR) p.clear_deployment = true;
    else if (d.value !== KEEP && d.value) p.deployment = d.value;
    return p;
  };

  const handleSave = async () => {
    if (!row) return;
    setMessage(null);

    // 1) Straight DB fields (through the existing save handler).
    const dbFields = {};
    DB_FIELD_DEFS.forEach(({ key, clearable, numeric, getValue }) => {
      const cur = String(dbForm[key] ?? "").trim();
      const og  = String(getValue(row) ?? "");
      if (clearable) { if (cur !== og) dbFields[key] = cur; return; }
      if (cur !== "" && cur !== og) dbFields[key] = numeric ? Number(cur) : cur;
    });

    setSaving(true);
    try {
      if (Object.keys(dbFields).length > 0) await onSave(dbFields);
    } catch {
      setSaving(false);
      setMessage({ severity: "error", text: "Could not save the details." });
      return;
    }
    setSaving(false);

    // 2) Overrides.
    if (activeGroups.length === 0) { onClose(); return; }

    setPending(true);
    try {
      // Saved straight to the document now (no MQTT round-trip, no ack to wait
      // for — see assets-ctrl.mjs's orionManualOverride) — success here means
      // it's already applied, not just "sent".
      const res = await API.orionManualOverride(buildOverridePayload(row.id));
      const body = res.data || res;

      if (body.success) {
        setMessage({ severity: "success", text: "Changes applied." });
        onOverrideSaved?.();
        setTimeout(() => onClose(), 800);
        return;
      }
      setMessage({ severity: "error", text: body.error || "Something went wrong applying the changes." });
    } catch (err) {
      setMessage({ severity: "error", text: err?.response?.data?.error || "Network error while applying the changes." });
    } finally {
      setPending(false);
    }
  };

  const changeChip =
    (dbChangedCount + activeGroups.length) > 0
      ? `${dbChangedCount + activeGroups.length} change${(dbChangedCount + activeGroups.length) > 1 ? "s" : ""}`
      : "No changes";

  return (
    <Dialog
      open={open}
      onClose={pending ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundColor: "#161616", backgroundImage: "none" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, borderBottom: `1px solid ${colors.grey[800]}`, pb: 1.5 }}>
        <EditIcon sx={{ color: "#459095", fontSize: 22 }} />
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: colors.grey[100] }}>Edit Asset Details</Typography>
          <Typography sx={{ fontSize: 12, color: colors.grey[400], mt: 0.2 }}>
            Correct fields the scan got wrong — leave anything else as-is.
          </Typography>
        </Box>
        <Chip
          label={changeChip}
          size="small"
          sx={{
            ml: "auto",
            bgcolor: (dbChangedCount + activeGroups.length) > 0 ? "#45909522" : "#ffffff0d",
            color:   (dbChangedCount + activeGroups.length) > 0 ? "#459095"   : colors.grey[400],
            fontSize: 11, fontWeight: 700,
          }}
        />
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: "#161616", pt: 3 }}>
        {message && (
          <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        )}

        <Typography sx={{ fontSize: 11, fontWeight: 700, color: colors.grey[400], letterSpacing: 0.6, mb: 1.25 }}>
          IDENTIFICATION
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1.25 }}>
          <OverrideSelect label="OS Family"  g={pin.os_family}  presets={OS_FAMILY_PRESETS} placeholder="e.g. Windows" currentValue={row?.os?.family} onChange={(patch) => setPinGroup("os_family", patch)} />
          <OverrideSelect label="OS Release" g={pin.os_version}                            placeholder="e.g. Windows 11 23H2" currentValue={row?.os?.version} onChange={(patch) => setPinGroup("os_version", patch)} />
          <OverrideSelect label="OS Build"   g={pin.os_build}                              placeholder="e.g. 22631" currentValue={row?.os?.build} onChange={(patch) => setPinGroup("os_build", patch)} />
          <ClassificationSelect g={pin.classification} row={row} onChange={(patch) => setPinGroup("classification", patch)} />
          <OverrideSelect label="Deployment" g={pin.deployment} presets={DEPLOYMENT_PRESETS} placeholder="e.g. virtual_machine" onChange={(patch) => setPinGroup("deployment", patch)} />
        </Box>

        <Divider sx={{ my: 2.5, borderColor: colors.grey[800] }} />

        <Typography sx={{ fontSize: 11, fontWeight: 700, color: colors.grey[400], letterSpacing: 0.6, mb: 1.25 }}>
          DETAILS
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1.5 }}>
          <TextField
            label="Custom Name" size="small" fullWidth
            placeholder="Friendly name for this asset…"
            helperText="Shown instead of the observed hostname wherever this asset appears"
            value={dbForm.machine_name} onChange={setDb("machine_name")}
          />
          <TextField
            label="Firmware" size="small" fullWidth
            value={dbForm["device.firmware"]} onChange={setDb("device.firmware")}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ backgroundColor: "#161616", borderTop: `1px solid ${colors.grey[800]}`, gap: 1 }}>
        {pending && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: "auto", pl: 1 }}>
            <CircularProgress size={14} thickness={5} />
            <Typography sx={{ fontSize: 12, color: colors.grey[300] }}>Applying…</Typography>
          </Box>
        )}
        <Button onClick={onClose} disabled={pending}>Cancel</Button>
        <Button variant="contained" disableElevation disabled={!canSubmit} onClick={handleSave}>
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditAssetDialog;
